import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { ChevronDown, Lightbulb, Lock, FileText, Download, X, Star, Briefcase } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

const bannerImage = "url('https://images.unsplash.com/photo-1556761175-5973dc0f32e7?q=80&w=2832&auto=format&fit=crop')";

const generalQuestions = [
  { id: 1, q: "Tell me about yourself.", a: "Start with your current role, mention key achievements, and explain why you're interested in this position." },
  { id: 2, q: "What are your strengths and weaknesses?", a: "Strengths: Adaptability, Teamwork. Weakness: I focus too much on details (turn it into a positive)." },
  { id: 3, q: "Where do you see yourself in 5 years?", a: "I see myself growing with the company, taking on more responsibilities, and mastering [Technology Name]." },
  { id: 4, q: "Why should we hire you?", a: "I have the right skills [Skill 1, Skill 2] and the passion to contribute to your team's success immediately." },
  { id: 5, q: "Explain the difference between Hard work and Smart work.", a: "Hard work is putting in the hours; Smart work is optimizing the process to achieve the same goal efficiently." },
  { id: 6, q: "What is Object-Oriented Programming (OOP)?", a: "OOP is a paradigm based on 'objects' containing data and code. Main pillars: Inheritance, Encapsulation, Polymorphism, Abstraction." },
  { id: 7, q: "Difference between Compiler and Interpreter?", a: "Compiler scans the whole program and translates it at once (e.g., C++). Interpreter translates one statement at a time (e.g., Python)." },
  { id: 8, q: "What is a Database Management System (DBMS)?", a: "DBMS is software for storing and retrieving users' data while considering appropriate security measures." }
];

const interviewSchema = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  "mainEntity": generalQuestions.map(q => ({
    "@type": "Question",
    "name": q.q,
    "acceptedAnswer": {
      "@type": "Answer",
      "text": q.a
    }
  }))
};

const InterviewQuestionsPage: React.FC = () => {
  const { user, token } = useAuth();
  const [premiumFiles, setPremiumFiles] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedFileId, setSelectedFileId] = useState<number | null>(null);
  const [accessKey, setAccessKey] = useState("");
  const [verifying, setVerifying] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");

  const [openIndex, setOpenIndex] = useState<number | null>(null);

  useEffect(() => {
    fetch('https://www.projectinfotech.in/api/interview-files')
        .then(res => res.json())
        .then(data => { setPremiumFiles(data); setLoading(false); })
        .catch(err => { console.error(err); setLoading(false); });
  }, []);

  const handleDownloadClick = (id: number) => {
      if (!user) { alert("Please login first."); return; }
      setSelectedFileId(id); setIsModalOpen(true); setErrorMsg(""); setAccessKey("");
  };

  const handleConfirmDownload = async (e: React.FormEvent) => {
      e.preventDefault(); setVerifying(true); setErrorMsg("");
      try {
          const res = await fetch(`https://www.projectinfotech.in/api/interview-files/download/${selectedFileId}`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
              body: JSON.stringify({ key: accessKey })
          });
          if (res.ok) {
              const blob = await res.blob();
              const url = window.URL.createObjectURL(blob);
              const a = document.createElement('a');
              a.href = url; a.download = "pit_interview_qa.pdf";
              document.body.appendChild(a); a.click(); a.remove(); setIsModalOpen(false);
          } else { setErrorMsg("Invalid Access Key or Server Error"); }
      } catch (err) { setErrorMsg("Connection Failed"); } 
      finally { setVerifying(false); }
  };

  return (
    <div className="min-h-screen bg-gray-50 pb-20 relative">
      <Helmet>
        <title>Tech Interview Questions & Answers | Placement Prep | PIT</title>
        <meta name="description" content="Prepare for your next IT interview with our curated list of HR and Technical questions. Download premium PIT interview resources for Java, Full Stack, and Python." />
        <script type="application/ld+json">{JSON.stringify(interviewSchema)}</script>
      </Helmet>

      <div className="absolute inset-0 z-0 opacity-30 pointer-events-none" style={{ backgroundImage: "radial-gradient(#cbd5e1 1px, transparent 1px)", backgroundSize: "24px 24px" }}></div>

      <div className="relative bg-gray-900 py-32 shadow-2xl bg-cover bg-center" style={{ backgroundImage: bannerImage, backgroundAttachment: 'fixed' }}>
        <div className="absolute inset-0 bg-black/60 backdrop-blur-[2px]"></div>
        <div className="relative max-w-7xl mx-auto px-4 text-center text-white pt-10 animate-fade-in-up">
          <div className="inline-flex items-center gap-2 bg-yellow-500/20 text-yellow-300 px-4 py-1.5 rounded-full text-sm font-bold mb-6 border border-yellow-500/30 backdrop-blur-md">
             <Star className="w-4 h-4 fill-current" aria-hidden="true" /><span>PIT Exclusive Resources</span>
          </div>
          <h1 className="text-5xl md:text-7xl font-extrabold mb-6 tracking-tight font-aligran drop-shadow-2xl">Ace Your Tech Interview</h1>
          <p className="text-xl text-gray-200 max-w-3xl mx-auto font-light leading-relaxed">From basic HR rounds to advanced technical deep-dives. We have compiled the most asked questions to help you succeed.</p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-16 grid lg:grid-cols-3 gap-10 -mt-20 relative z-10">
        
        {/* LEFT: GENERAL QUESTIONS */}
        <div className="lg:col-span-2 space-y-6">
            <div className="bg-white p-6 rounded-2xl shadow-lg border-l-8 border-blue-600 flex items-center gap-4">
                <div className="p-3 bg-blue-100 text-blue-700 rounded-full"><Briefcase size={28} aria-hidden="true" /></div>
                <div><h2 className="text-2xl font-bold text-gray-800">General Questions</h2><p className="text-sm text-gray-500">Must-know questions for every interview round.</p></div>
            </div>
            
            <div className="space-y-4">
                {generalQuestions.map((q, idx) => (
                    <div key={q.id} className={`group bg-white rounded-xl border border-gray-200 overflow-hidden transition-all duration-300 ${openIndex === idx ? 'shadow-lg ring-1 ring-blue-200' : 'hover:shadow-md'}`}>
                        <button aria-expanded={openIndex === idx} onClick={() => setOpenIndex(openIndex === idx ? null : idx)} className="w-full flex justify-between items-start p-5 text-left focus:outline-none">
                            <div className="flex gap-4">
                                <span className={`flex items-center justify-center w-8 h-8 rounded-full text-sm font-bold shrink-0 transition-colors ${openIndex === idx ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-500 group-hover:bg-blue-50 group-hover:text-blue-600'}`}>{idx + 1}</span>
                                <span className={`font-semibold text-lg transition-colors pt-0.5 ${openIndex === idx ? 'text-blue-800' : 'text-gray-700 group-hover:text-blue-600'}`}>{q.q}</span>
                            </div>
                            <div className={`mt-1 ml-4 transition-transform duration-300 ${openIndex === idx ? 'rotate-180 text-blue-600' : 'text-gray-400'}`}>
                                <ChevronDown size={20} aria-hidden="true"/>
                            </div>
                        </button>
                        <div className={`overflow-hidden transition-all duration-300 ease-in-out ${openIndex === idx ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'}`}>
                            <div className="p-5 pt-0 pl-[4.5rem] pr-8 pb-6">
                                <div className="bg-blue-50/50 p-4 rounded-lg border border-blue-100 text-gray-700 leading-relaxed text-sm">
                                    <strong className="block text-blue-900 text-xs uppercase tracking-wide mb-2 flex items-center gap-2">
                                        <Lightbulb size={14} className="text-yellow-500" aria-hidden="true" /> Suggested Answer
                                    </strong>
                                    {q.a}
                                </div>
                            </div>
                        </div>
                    </div>
                ))}
            </div>
        </div>

        {/* RIGHT: PIT QUESTIONS */}
        <div className="lg:col-span-1">
            <div className="bg-gray-900 rounded-2xl shadow-2xl border border-gray-800 sticky top-24 overflow-hidden ring-4 ring-gray-900/10">
                <div className="relative p-8 text-white overflow-hidden">
                    <div className="absolute -top-10 -right-10 w-32 h-32 bg-red-600 rounded-full blur-[60px] opacity-40"></div>
                    <div className="absolute -bottom-10 -left-10 w-32 h-32 bg-blue-600 rounded-full blur-[60px] opacity-40"></div>
                    <h3 className="text-2xl font-bold flex items-center gap-3 relative z-10"><Lock className="text-red-500" size={24} aria-hidden="true" /> <span className="bg-clip-text text-transparent bg-gradient-to-r from-white to-gray-400">PIT Questions</span></h3>
                    <p className="text-sm text-gray-400 mt-2 relative z-10 leading-relaxed">Exclusive question sets curated by PIT experts. Contains <strong>Technical & Scenario-based</strong> questions.</p>
                </div>
                <div className="p-4 bg-gray-800/50 min-h-[350px]">
                    {loading ? (
                        <div className="space-y-3">{[1,2,3].map(i => <div key={i} className="h-16 bg-gray-800 rounded-xl animate-pulse"></div>)}</div>
                    ) : premiumFiles.length === 0 ? (
                        <div className="text-center py-12 opacity-50"><FileText size={48} className="mx-auto mb-3 text-gray-600" aria-hidden="true" /><p className="text-sm text-gray-500">No PIT questions uploaded yet.</p></div>
                    ) : (
                        <div className="space-y-3">
                            {premiumFiles.map((file) => (
                                <div key={file.id} className="group bg-gray-800 p-4 rounded-xl border border-gray-700 hover:border-red-500/50 hover:bg-gray-750 transition-all duration-300 flex justify-between items-center relative overflow-hidden">
                                    <div className="flex items-center gap-4 relative z-10 min-w-0">
                                        <div className="w-10 h-10 rounded-lg bg-gray-900 flex items-center justify-center text-red-500 border border-gray-700 shadow-inner group-hover:scale-110 transition-transform"><FileText size={20} aria-hidden="true" /></div>
                                        <div className="min-w-0">
                                            <h4 className="font-bold text-gray-200 text-sm truncate pr-2 group-hover:text-white transition-colors">{file.title}</h4>
                                            <span className="inline-flex items-center text-[10px] uppercase font-bold tracking-wider text-gray-500 bg-gray-900 px-2 py-0.5 rounded mt-1 border border-gray-700">{file.subject}</span>
                                        </div>
                                    </div>
                                    <button onClick={() => handleDownloadClick(file.id)} className="relative z-10 w-9 h-9 flex items-center justify-center rounded-full bg-gray-700 text-gray-400 hover:bg-red-600 hover:text-white transition-all shadow-lg group-hover:scale-105" title={`Download ${file.title} PDF`} aria-label={`Download ${file.title}`}>
                                        <Download size={16} aria-hidden="true" />
                                    </button>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
                <div className="p-4 border-t border-gray-800 bg-gray-900 text-gray-500 text-xs flex items-center justify-center gap-2"><Lock size={12} aria-hidden="true" /><span>Access Key required for download</span></div>
            </div>
        </div>
      </div>

      {/* SECURE DOWNLOAD MODAL */}
      {isModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm p-4 animate-fade-in" role="dialog" aria-modal="true">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm relative overflow-hidden transform transition-all scale-100">
                <div className="bg-gray-900 p-6 text-center text-white relative">
                    <button onClick={() => setIsModalOpen(false)} aria-label="Close Modal" className="absolute top-4 right-4 text-white/50 hover:text-white transition"><X size={20} aria-hidden="true" /></button>
                    <div className="w-16 h-16 bg-gradient-to-br from-red-600 to-red-800 rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg border-4 border-gray-800"><Lock size={32} className="text-white" aria-hidden="true" /></div>
                    <h3 className="text-xl font-bold">Secure Download</h3>
                    <p className="text-xs text-gray-400 mt-1">Unlock "PIT Questions" PDF</p>
                </div>
                <div className="p-8">
                    <form onSubmit={handleConfirmDownload}>
                        <label className="block text-xs font-bold text-gray-500 uppercase tracking-wide mb-2 text-center">Enter Your Student Key</label>
                        <input type="text" placeholder="e.g. PIT-KEY-2026" aria-label="Student Access Key" className="w-full p-4 bg-gray-50 border border-gray-300 rounded-xl mb-6 focus:ring-2 focus:ring-red-500 focus:border-red-500 outline-none text-center font-mono text-lg tracking-widest text-gray-800 placeholder-gray-400 transition-all uppercase" value={accessKey} onChange={e => setAccessKey(e.target.value)} required autoFocus />
                        {errorMsg && (
                            <div className="mb-4 p-3 bg-red-50 text-red-600 text-xs rounded-lg flex items-center gap-2 border border-red-100 justify-center font-semibold" role="alert">
                                <span className="w-2 h-2 bg-red-600 rounded-full animate-pulse"></span>{errorMsg}
                            </div>
                        )}
                        <button type="submit" disabled={verifying} className="w-full bg-red-600 text-white py-3.5 rounded-xl font-bold hover:shadow-lg hover:bg-red-700 transition-all disabled:opacity-70 disabled:cursor-not-allowed flex items-center justify-center gap-2">
                            {verifying ? "Verifying Key..." : "Unlock File"}
                        </button>
                    </form>
                </div>
            </div>
        </div>
      )}
    </div>
  );
};

export default InterviewQuestionsPage;